import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { OneLPSsdkParamsContext } from '../../onelps-sdk-params-context';
import Logo from '../common/Logo';
import AlreadyRegisteredLink, {
	ALREADY_REGISTERED_LINK_POSITION,
} from '../common/AlreadyRegisteredLink';
import AlreadyRegisteredLogin from '../headers/AlreadyRegisteredLogin';
import ExtendedLogin from '../forms/ExtendedLogin';
import LoginForm from '../forms/LoginForm';
import Footer from '../footers/StandardFooter';
import styles from '../../css/components/main-wrappers/standardPageWrapper.css';
import FoeStellarAgeHeader from '../content/FoeStellarAgeHeader.js';
import HTMLSnippet from '../snippets/HTMLSnippet';
import { AppContext } from '../../Store';
import FieldStoresWrapper from './FieldStoresWrapper';
import PortalBar from '../headers/PortalBar';
import Modal from '../modals/Modal';
import RememberMe from '../remember-me/RememberMe';
import { useTranslation } from 'react-i18next';
import { withCommonPageFunctionality } from './helpers/commonPageFunctionality';
import PasswordForgottenStepTwo, {
	STEP2_SERVER_SUCCESS_RELOAD_AFTER,
} from '../password-forgotten/Step2';
import SocialMediaList from '../common/SocialMediaLinkListContainer';
import ServerMessages from '../common/ServerMessages';
import BetaBadge from '../common/BetaBadge';
import { getAssetUrl } from '../../helpers/get-asset-url';
import NoRegFormLinks from '../common/links/NoRegFormLinks';
import PartnerLogo from '../common/PartnerLogo';
import PartnerBar from '../common/PartnerBar';
import OneStepForm from '../forms/standard/OneStepForm';
import TwoStepForm from '../forms/standard/TwoStepForm';
import OneClickForm from '../forms/standard/OneClickForm';
import { withOneStepValidation } from '../../helpers/validation/one-step-forms/1step-form-with-nickname-validation';
import { withOneStepValidationWithEmail } from '../../helpers/validation/one-step-forms/1step-form-with-email-validation';
import { withOneStepValidationWithAllFields } from '../../helpers/validation/one-step-forms/1step-form-with-all-fields';
import { withOneClickValidation } from '../../helpers/validation/1click-form-validation';
import OldReturningUserFlow from '../overlays/OldReturningUserFlow';
import {
	COOKIE_NOTIFICATION_NAME,
	COOKIE_NOTIFICATION_VALUE_ACCEPT,
	COOKIE_NOTIFICATION_EXPIRATION_DATE,
	withCookieOverlayWithoutRegAttempt,
} from '../../helpers/cookie-overlay';
import PreloadWrapper from '../common/PreloadWrapper';
import SsoConfirmationOverlay from '../overlays/SsoConfirmationOverlay';
import { getSsoConfirmationUrl } from '../../helpers/sso-url';
import CookieOverlayExtendedBottom from '../overlays/CookieOverlayExtendedBottom';
import { cookieExist, getCookieValue, setCookie } from '../../helpers/browser-cookies';
import FaroComponent from '../common/FaroComponent';
import SsoSetPasswordForm from '../overlays/SsoSetPasswordForm';
import CTAButton from '../buttons/CTAButton';
import Tagline from '../common/Tagline';
import UskLabelSmall from '../common/UskLabelSmall';
import PreLander from '../pre-lander/PreLander';

function StandardPageWrapper({
	isAuthenticated,
	username = '',
	forgotPwToken = null,
	hasForgotPwError = null,
	showFooter = true,
	shouldShowCookieOverlay,
	onOverlayCTAButtonClick,
	onRejectButtonClick,
}) {
	const { t } = useTranslation('translation');

	const [showChangePasswordOverlay, setShowChangePasswordOverlay] =
		useState(true);

	const [state, dispatch] = useContext(AppContext);
	const onelpsParams = useContext(OneLPSsdkParamsContext);
	const optionalSnippets = onelpsParams.snippets.optional;
	const mandatorySnippets = onelpsParams.snippets.mandatory;
	const isClosedOrOpenBetaOrPreRegister =
		onelpsParams.launchPhase &&
		(onelpsParams.launchPhase.phase === 'isClosedBeta' ||
			onelpsParams.launchPhase.phase === 'isOpenBeta' ||
			onelpsParams.launchPhase.phase === 'isPreRegister');
	const isPreRegister =
		onelpsParams.launchPhase &&
		onelpsParams.launchPhase.phase === 'isPreRegister';
	const hasSupportButton =
		onelpsParams.launchPhase && onelpsParams.launchPhase.hasSupportButton && onelpsParams.launchPhase.buttonLabelTranslationKey && onelpsParams.launchPhase.buttonLink;

const isPreloadingAssets = onelpsParams.preload.show || false;

	const isLoginFormOnPage = onelpsParams.loginForm.type === 'onPage';
	const isLoginFormLink = onelpsParams.loginForm.type === 'link';
	const isLoginFormNotInHeader =
		onelpsParams.loginForm.type === 'tab' || onelpsParams.loginForm.type === 'oneClick' || onelpsParams.loginForm.type === '';

	const isCrossPlatformUser = !!(onelpsParams.portalSsoError && onelpsParams.portalSsoError.type);

	const withCookieOverlay = withCookieOverlayWithoutRegAttempt;

	const formConfig = useMemo(() => {
		let formComponent;
		let formProps = { hasEmail: false, hasNickname: false };

		const formType = onelpsParams.form.type;
		const formSteps = onelpsParams.form.steps;
		const OneStepFormWithNicknameValidation =
			withOneStepValidation(OneStepForm);
		const OneStepFormWithEmailValidation =
			withOneStepValidationWithEmail(OneStepForm);
		const OneStepFormWithAllFieldsValidation =
			withOneStepValidationWithAllFields(OneStepForm);
		const OneClickFormWithValidation = withOneClickValidation(OneClickForm);

		switch (formType) {
			case 'regWithAllFields':
				formProps.hasNickname = true;
				formProps.hasEmail = true;

				if (formSteps === 1) {
					formComponent = withCookieOverlay(OneStepFormWithAllFieldsValidation);
				} else {
					formComponent = withCookieOverlay(TwoStepForm);
				}
				break;
			case 'regWithoutEmail':
				formComponent = withCookieOverlay(OneStepFormWithNicknameValidation);
				formProps.hasNickname = true;
				formProps.hasEmail = false;
				break;
			case 'regWithoutNickname':
				formComponent = withCookieOverlay(OneStepFormWithEmailValidation);
				formProps.hasNickname = false;
				formProps.hasEmail = true;
				break;
			case 'regWithOneClick':
				formComponent = withCookieOverlay(OneClickFormWithValidation);
				formProps.hasNickname = false;
				formProps.hasEmail = false;
				break;
			default:
				break;
		}
		return { formComponent, formProps };
	}, [onelpsParams.form, withCookieOverlay]);

	const handleConfirmSsoLogin = async () => {
		window.location.href = getSsoConfirmationUrl(
			window.location.href,
			onelpsParams.ssoConfirmationPath
		);
	};

	const handleEnterPassword = async (password) => {
		window.location.href = getSsoConfirmationUrl(
			window.location.href,
			onelpsParams.ssoConfirmationPath,
			password
		);
	};

	useEffect(() => {
		if (isCrossPlatformUser) {
			setCookie(COOKIE_NOTIFICATION_NAME, COOKIE_NOTIFICATION_VALUE_ACCEPT, COOKIE_NOTIFICATION_EXPIRATION_DATE);
			dispatch({ type: 'ACCEPT_THIRD_PARTY_TRACKING' });
		}
		if (!cookieExist(COOKIE_NOTIFICATION_NAME)) {
			dispatch({ type: 'SHOW_COOKIE_OVERLAY' });
		} else if (
			getCookieValue(COOKIE_NOTIFICATION_NAME) ===
			COOKIE_NOTIFICATION_VALUE_ACCEPT
		) {
			dispatch({ type: 'ACCEPT_THIRD_PARTY_TRACKING' });
		}
	}, [dispatch, isCrossPlatformUser]);

	const isStellarAge = onelpsParams.abTestFeatureFlags?.['stellarAgeCustomStyle'] === true;
	const showUskLabel = onelpsParams.uskLabel?.show && onelpsParams.uskLabel?.standard && onelpsParams.country === 'DE';

	const isPreLanderActive = onelpsParams.preLander?.show && !state.preLanderCompleted;
	const showMainContent = !isPreLanderActive;


	return (
		<div
			id={onelpsParams.gameId}
			className={`${styles.page} ${onelpsParams.bigbeat ? styles[onelpsParams.bigbeat] : ''} ${styles[onelpsParams.gameId]} ${state.modalShown ? 'modalShown' : 'modalHidden'
				}`}
		>
			{onelpsParams.partner?.show && onelpsParams.partner.type === 'logo' && (
				<PartnerLogo />
			)}

			{onelpsParams.partner?.show &&
				onelpsParams.partner.type === 'iframeBar' && (
					<iframe
						src={onelpsParams.partner.url}
						id={`iframe-${onelpsParams.partner.name}`}
						title={onelpsParams.partner.name}
						marginWidth="0"
						marginHeight="0"
						width="100%"
						height="60"
						scrolling="no"
						border="0"
						frameBorder="0"
						style={{
							padding: 0,
							margin: 0,
							border: 0,
							position: 'relative',
							zIndex: 9999,
						}}
					></iframe>
				)}

			{onelpsParams.partner?.show && onelpsParams.partner.type === 'bar' && (
				<PartnerBar
					url={onelpsParams.partner.url}
					text={onelpsParams.partner.text}
					name={onelpsParams.partner.name}
				/>
			)}

			{/* {
				<div
					className={`${styles.loadingOverlay} ${state.translationsLoaded ? styles.allLoaded : ''
					}`}
				></div>
			} */}
			{onelpsParams.portalBar.show && <PortalBar />}

			{onelpsParams.preLander?.show && <PreLander />}

			{onelpsParams.additionalCharacter && (
				<img
					className={styles.additionalCharacter}
					src={
						getAssetUrl() +
						require('../../img/' +
							GAMEID +
							'/characters/' +
							onelpsParams.additionalCharacter)
					}
				></img>
			)}

			{isStellarAge && (
				<FoeStellarAgeHeader/>
			)}

			{onelpsParams.loginForm.show &&
				isLoginFormOnPage &&
				onelpsParams.loginForm.extendedLogin && (
					<div className={styles.headerWrapperStandard}>
						<ExtendedLogin
							showForgotPassword={onelpsParams.loginForm.showForgotPassword}
							showExtendedByDefault={
								onelpsParams.loginForm.showExtendedByDefault
							}
							showRememberMe
						/>
					</div>
				)}
			{onelpsParams.loginForm.show &&
				isLoginFormOnPage &&
				onelpsParams.loginForm.design === 'fieldsAlwaysVisible' &&
				!onelpsParams.loginForm.extendedLogin && (
					<div className={styles.headerWrapperStandard}>
						<LoginForm
							playerIdentifierInputId="page_login_always-visible_input_player-identifier"
							passwordInputId="page_login_always-visible_input_password"
							buttonId="page_login_always-visible_button_login"
						/>
					</div>
				)}
			{onelpsParams.loginForm.show && isLoginFormLink && (
				<div className={styles.headerWrapperRight}>
					<div className={styles.headerWrapperStandard}>
						<AlreadyRegisteredLink
							linkText={t('loginForm.alreadyRegistered')}
							position={ALREADY_REGISTERED_LINK_POSITION.HEADER}
						/>
					</div>
				</div>
			)}
			{onelpsParams.loginForm.show &&
				isLoginFormOnPage &&
				onelpsParams.loginForm.design === 'linkAlreadyRegistered' && (
					<div className={styles.loginWrapper}>
						<AlreadyRegisteredLogin />
					</div>
				)}

			{onelpsParams.tagline?.show && <Tagline />}

			{onelpsParams.logo.show && (
				<div
					className={`${styles.logoWrapper} ${onelpsParams.bigbeat ? (isPreLanderActive ? styles[onelpsParams.bigbeat] : styles.bigbeat) : ''}  ${isLoginFormNotInHeader ? styles.extraTopMargin : ''
					} ${isStellarAge ? styles.stellarAge : ''}`}
				>
					<Logo
						gameId={onelpsParams.gameId}
						title={onelpsParams.title} />
				</div>
			)}
			{onelpsParams.socialMedia.show &&
				onelpsParams.socialMedia.position === 'panel-left' && (
					<div className={styles.socialMediaList}>
						<SocialMediaList gameId={onelpsParams.gameId} />
					</div>
				)}

			<div className={`${styles.main} ${onelpsParams.bigbeat ? styles.bigbeat : ''} ${isStellarAge ? styles.stellarAge : ''}`}>
				{!onelpsParams.oldReturningUserFlow &&
					isAuthenticated &&
					state.showRememberMeModal && (
						<Modal
							isShownAsOverlay={true}
							hasCloseButton={false}
							title={t('rememberMe.title')}
						>
							<RememberMe userName={username} />
						</Modal>
					)}

				{onelpsParams.portalSsoError &&
					onelpsParams.portalSsoError.type &&
					!state.showRememberMeModal && (
						<Modal
							isShownAsOverlay={true}
							hasCloseButton={false}
							title={t(onelpsParams.portalSsoError.translationKey)}
							shouldCloseByClickOutside={
								onelpsParams.forgotPasswordOverlay.closeByClickOutside
							}
						>
							{(() => {
								switch (onelpsParams.portalSsoError.type) {
									case 'CONFIRMATION_NEEDED':
										return (
											<SsoConfirmationOverlay
												handleConfirmSsoLogin={handleConfirmSsoLogin}
											/>
										);
									case 'PASSWORD_NEEDED':
										return (
											<SsoSetPasswordForm
												onCtaButtonPressCallback={handleEnterPassword}
											/>
										);
								}
							})()}
						</Modal>
					)}
				{onelpsParams.oldReturningUserFlow && (
					<OldReturningUserFlow isAuthenticated={isAuthenticated} />
				)}

				{showMainContent && onelpsParams.form.show && (
					<>
						<FieldStoresWrapper>
							<formConfig.formComponent {...formConfig.formProps} />
						</FieldStoresWrapper>
					</>
				)}

				{showMainContent && showUskLabel && <UskLabelSmall />}

				{showMainContent && isClosedOrOpenBetaOrPreRegister && (
					<>
						<Modal
							isShownAsOverlay={false}
							hasCloseButton={false}
							title={
								!isPreRegister
									? t('noRegForm.title')
									: t(`oneStepForm.header.${onelpsParams.gameId}`)
							}
						>
							<NoRegFormLinks
								gameName={onelpsParams.title}
								betaPhase={onelpsParams.launchPhase.phase}
								launchPhaseTranslationKey={
									onelpsParams.launchPhase.translationKey
								}
							/>
						</Modal>
						{hasSupportButton && (
							<div className={styles.supportButtonContainer}>
								<CTAButton
									buttonLabel={t(onelpsParams.launchPhase.buttonLabelTranslationKey)}
									onButtonClick={() => { window.top.location.href = onelpsParams.launchPhase.buttonLink; }}
								/>
							</div>
						)}
						;
					</>
				)}
			</div>
			{forgotPwToken && !hasForgotPwError && showChangePasswordOverlay && (
				<Modal
					isShownAsOverlay={true}
					hasCloseButton={
						onelpsParams.loginForm.showSuccessMessageInForgotPasswordOverlay
					}
					title={t('passwordForgotten.changePassword.title')}
					closeCallback={() => setShowChangePasswordOverlay(false)}
					shouldCloseByClickOutside={
						onelpsParams.forgotPasswordOverlay.closeByClickOutside
					}
				>
					<PasswordForgottenStepTwo
						onSuccessCallback={() => {
							if (
								!onelpsParams.loginForm
									.showSuccessMessageInForgotPasswordOverlay
							) {
								setShowChangePasswordOverlay(false);
								setTimeout(function() {
									window.location.href = window.location.origin;
								}, STEP2_SERVER_SUCCESS_RELOAD_AFTER);
							}
						}}
						showSuccessMessageInModal={
							onelpsParams.loginForm.showSuccessMessageInForgotPasswordOverlay
						}
					/>
				</Modal>
			)}

			{onelpsParams.launchPhase.showBadge && (
				<BetaBadge
					phase={onelpsParams.launchPhase.phase}
					imgSrc={onelpsParams.launchPhase.badgeSrc}
				/>
			)}

			{showFooter && onelpsParams.footer.show && <Footer showLogo />}
			{mandatorySnippets &&
				mandatorySnippets.map((snippet, i) => {
					if (snippet) {
						return <HTMLSnippet
							key={i}
							snippet={snippet} />;
					}
				})}
			{state.thirdPartyTrackingAccepted &&
				optionalSnippets &&
				optionalSnippets.length > 0 &&
				optionalSnippets.map((snippet, i) => {
					if (snippet) {
						return (
							<HTMLSnippet
								key={i}
								snippet={snippet}
								delay={1000}
								callback={() => {
									dispatch({ type: 'MARK_TRACKING_EXECUTED' });
								}}
							/>
						);
					}
				})}
			<ServerMessages
				excludeEmailConfirmation={onelpsParams.oldReturningUserFlow}
			/>
			<ServerMessages />

			{isPreloadingAssets && <PreloadWrapper />}
			{onelpsParams.cookieOverlay.show && shouldShowCookieOverlay && (
				<div className={shouldShowCookieOverlay ? styles.show : styles.hide}>
					{onelpsParams.cookieOverlay.type === 'settings' &&
						onelpsParams.cookieOverlay.design === 'text' &&
						onelpsParams.cookieOverlay.position === 'bottom' && (
							<CookieOverlayExtendedBottom
								onCTAButtonClick={onOverlayCTAButtonClick}
								privacyHref={state.privacyUrl}
								imprintHref={state.imprintUrl}
								onRejectCookies={onRejectButtonClick}
							/>
						)}
				</div>
			)}

			{onelpsParams.faro?.load && <FaroComponent />}
		</div>
	);
}

StandardPageWrapper.propTypes = {
	username: PropTypes.string,
	isAuthenticated: PropTypes.bool,
	forgotPwToken: PropTypes.string,
	hasForgotPwError: PropTypes.bool,
	SocialMediaList: PropTypes.string,
	showFooter: PropTypes.bool,
	shouldShowCookieOverlay: PropTypes.bool.isRequired,
	onOverlayCTAButtonClick: PropTypes.func.isRequired,
	onRejectButtonClick: PropTypes.func.isRequired,
};

const EnhancedStandardPageWrapper = withCookieOverlayWithoutRegAttempt(
	withCommonPageFunctionality(StandardPageWrapper)
);

export default EnhancedStandardPageWrapper;
